import React from "react";
import { Link } from "wouter";
import { useLocation } from "wouter";

// Custom navigation component for hash links
const HashLink = ({ href, children, className }: { href: string; children: React.ReactNode; className: string }) => {
  const [location, setLocation] = useLocation();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Extract the hash from the href
    const hash = href.split('#')[1];
    
    if (location === '/') {
      // Already on home page, just scroll to section
      const section = document.getElementById(hash);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
      // Update URL without reload
      window.history.pushState(null, '', href);
    } else {
      // Navigate to home page with hash
      setLocation('/');
      // Set a flag to scroll after navigation
      sessionStorage.setItem(`scrollTo${hash.charAt(0).toUpperCase() + hash.slice(1)}`, 'true');
      // Update URL
      window.history.pushState(null, '', href);
    }
  };
  
  return (
    <a href={href} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

const Footer = () => {
  return (
    <footer className="bg-neutral text-white pt-12 pb-6">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center mb-4">
            <img 
               src="/assets/Logo-tr-BxLhrsue.png" 
               alt="ZiwaTech Logo" 
               className="h-8 w-auto mr-2"
            />
              <h3 className="text-green-400 font-bold text-primary">
              <span className="text-[#28c77b] font-semibold tracking-tight text-sm">
                <span className="block">Repair Store</span>
                <span className="text-[#28c77b] block">North York, YorkgateMall</span>
              </span>
              </h3>
            </div>
            <p className="mb-4 text-gray-400">
              Professional device repair services for smartphones, computers, tablets, and game consoles.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=100089098347575" className="text-gray-400 hover:text-primary transition-colors">
                <i className="fab fa-facebook-f"></i>
              </a>
              <a href="https://twitter.com/ziwatech" className="text-gray-400 hover:text-primary transition-colors">
                <i className="fab fa-twitter"></i>
              </a>
              <a href="https://www.instagram.com/ziwatech_cellphone_computers/" className="text-gray-400 hover:text-primary transition-colors">
                <i className="fab fa-instagram"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-primary transition-colors">
                <i className="fab fa-linkedin-in"></i>
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-primary">Repair Services</h3>
            <ul className="space-y-2">
              <li>
                <HashLink href="/#services" className="text-gray-400 hover:text-primary transition-colors">
                  Phone Repair
                </HashLink>
              </li>
              <li>
                <HashLink href="/#services" className="text-gray-400 hover:text-primary transition-colors">
                  Computer Repair
                </HashLink>
              </li>
              <li>
                <HashLink href="/#services" className="text-gray-400 hover:text-primary transition-colors">
                  Tablet Repair
                </HashLink>
              </li>
              <li>
                <HashLink href="/#services" className="text-gray-400 hover:text-primary transition-colors">
                  Game Console Repair
                </HashLink>
              </li>
              <li>
                <HashLink href="/#services" className="text-gray-400 hover:text-primary transition-colors">
                  Data Recovery
                </HashLink>
              </li>
              <li>
                <HashLink href="/#services" className="text-gray-400 hover:text-primary transition-colors">
                  Water Damage Repair
                </HashLink>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-primary">Support</h3>
            <ul className="space-y-2">
              <li>
                <Link href="/contact" className="text-gray-400 hover:text-primary transition-colors" onClick={() => {
                  // Scroll to top when navigating to contact
                  window.scrollTo(0, 0);
                }}>
                  Contact Us
                </Link>
              </li>
              <li>
                <Link href="/booking" className="text-gray-400 hover:text-primary transition-colors" onClick={() => {
                  // Scroll to top when navigating to booking
                  window.scrollTo(0, 0);
                }}>
                  Book a Repair
                </Link>
              </li>
              <li>
                <HashLink href="/#faq" className="text-gray-400 hover:text-primary transition-colors">
                  FAQ
                </HashLink>
              </li>
              <li>
                <Link href="/services" className="text-gray-400 hover:text-primary transition-colors" onClick={() => {
                  // Scroll to top when navigating to services
                  window.scrollTo(0, 0);
                }}>
                  Warranty Information
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-gray-400 hover:text-primary transition-colors" onClick={() => {
                  // Scroll to top when navigating to contact
                  window.scrollTo(0, 0);
                }}>
                  Status Check
                </Link>
              </li>
              <li>
                <Link href="/about-us" className="text-gray-400 hover:text-primary transition-colors" onClick={() => {
                  // Scroll to top when navigating to about-us
                  window.scrollTo(0, 0);
                }}>
                  About Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-primary">Contact Us</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <i className="fas fa-map-marker-alt mt-1 mr-3 text-primary"></i>
                <span className="text-gray-400">
                A140-1 York Gate Blvd,
                North York, Toronto, 
                ON M3N 3A1, Canada
                </span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-phone-alt mr-3 text-primary"></i>
                <span className="text-gray-400">+1647-200-2040</span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-envelope mr-3 text-primary"></i>
                <span className="text-gray-400">repair@ziwatech.ca</span>
              </li>
              <li className="space-y-2">
                <div className="flex items-center">
                  <i className="fas fa-clock mr-3 text-primary"></i>
                  <span className="text-gray-400">Mon-Fri: 10am-8pm</span>
                </div>
                <div className="flex items-center ml-8">
                  <span className="text-gray-400">Sat: 10am-6pm</span>
                </div>
                <div className="flex items-center ml-8">
                  <span className="text-gray-400">Sun: 12pm-5pm</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-600 pt-6 mt-6">
          <div className="flex flex-col md:flex-row justify-between">
            <p className="text-gray-400 text-sm mb-4 md:mb-0">&copy; {new Date().getFullYear()} ZiwaTech. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link href="/privacy-policy" className="text-gray-400 hover:text-primary transition-colors text-sm" onClick={() => {
                window.scrollTo(0, 0);
              }}>
                Privacy Policy
              </Link>
              <Link href="/terms-of-service" className="text-gray-400 hover:text-primary transition-colors text-sm" onClick={() => {
                window.scrollTo(0, 0);
              }}>
                Terms of Service
              </Link>
              <Link href="/cookie-policy" className="text-gray-400 hover:text-primary transition-colors text-sm" onClick={() => {
                window.scrollTo(0, 0);
              }}>
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
